/** Portail — conditions commerciales du client connecté (accords actifs uniquement ; jamais ceux d'un autre client). */
import { useQuery } from '@tanstack/react-query';
import { Badge, Card, Table } from '@myron/design-system';
import { Link } from 'react-router';
import { get } from '../../../shared/api/client';
import { EntetePage, EtatRequete } from '../../../shared/ui/composants';
import { dateCourte, euros, LIBELLE_CONDITIONNEMENT } from '../../../shared/ui/format';
import { libellePrix, libelleQuantites } from '../../admin/accords/api';

interface LignePortail { id: string; cible: { type: string; libelle: string; produits: { id: string; sku: string; libelle: string }[] }; conditionnementType: string; prixHtCentimes: number | null; remiseBp: number | null; quantiteMin: number | null; quantiteMax: number | null }
interface AccordPortail { id: string; reference: string; intitule: string; valideDu: string; valideAu: string | null; cumulPromotions: 'AUCUN' | 'MEILLEUR_PRIX'; conditionsParticulieres: string | null; enVigueur: boolean; lignes: LignePortail[] }

export function MesConditions() {
  const q = useQuery({ queryKey: ['pro', 'conditions'], queryFn: () => get<{ segment: string; donnees: AccordPortail[] }>('/pro/conditions') });
  return (
    <>
      <EntetePage titre="Mes conditions commerciales" sousTitre="Prix et quantités négociés avec MYRON. Ils sont appliqués automatiquement dans le catalogue et le panier." />
      <EtatRequete requete={q} vide={(d) => d.donnees.length === 0} titreVide="Aucun accord en cours"
        texteVide={<>Vous bénéficiez du tarif professionnel standard et des promotions du catalogue. Pour un volume important, <Link to="/pro/devis/nouveau">demandez un devis</Link>.</>}
        enfants={(d) => (
          <div className="my-pile">
            {d.donnees.map((a) => (
              <Card key={a.id}>
                <div className="my-ligne my-ligne--espace">
                  <h2 className="my-titre-3">{a.intitule}</h2>
                  {a.enVigueur ? <Badge ton="succes">En vigueur</Badge> : <Badge ton="info">À venir</Badge>}
                </div>
                <p className="my-secondaire">Référence {a.reference} · {a.valideAu ? `du ${dateCourte(a.valideDu)} au ${dateCourte(a.valideAu)}` : `depuis le ${dateCourte(a.valideDu)}`} · {a.cumulPromotions === 'MEILLEUR_PRIX' ? 'vous bénéficiez de la promotion si elle est plus avantageuse' : 'prix de l’accord hors promotions'}</p>
                {a.conditionsParticulieres && <p>{a.conditionsParticulieres}</p>}
                <Table<LignePortail> legende={`Lignes de l’accord ${a.reference}`} lignes={a.lignes} cle={(l) => l.id} colonnes={[
                  { cle: 'c', titre: 'Produits', rendu: (l) => <>{l.cible.libelle}{l.cible.type === 'SELECTION' && <><br /><span className="my-legende my-secondaire">{l.cible.produits.map((p) => p.libelle).join(', ')}</span></>}</> },
                  { cle: 't', titre: 'Conditionnement', rendu: (l) => LIBELLE_CONDITIONNEMENT[l.conditionnementType] ?? l.conditionnementType },
                  { cle: 'p', titre: 'Prix', rendu: (l) => libellePrix(l, euros) },
                  { cle: 'q', titre: 'Quantité par commande', rendu: libelleQuantites },
                ]} />
              </Card>
            ))}
            <p className="my-secondaire my-petit">Au-delà des quantités prévues, <Link to="/pro/devis/nouveau">demandez un devis</Link>.</p>
          </div>
        )} />
    </>
  );
}
